import { useRef, useState } from "react";
import Header from "../../components/Header/Header";
import SideNavbar from "../../components/Navbar/SideNavbar";
import { apis } from "../../../core/services/params";
import { toast } from 'react-hot-toast';



const FileUploaderComponent = () => {

    const [fileUrl, setFileUrl] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileType, setFileType] = useState<'image' | 'csv' | 'xlsx' | 'other'>('other');
    const urlInputRef = useRef<HTMLInputElement | null>(null);


    const getFileType = (file: File): 'image' | 'csv' | 'xlsx' | 'other' => {
        if (file.type.startsWith('image/')) {
            return 'image';
        } else if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
            return 'csv';
        } else if (
            file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            file.name.toLowerCase().endsWith('.xlsx')
        ) {
            return 'xlsx';
        }
        return 'other';
    };

    const isValidFileType = (file: File): boolean => {
        const validTypes = [
            // Images
            'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
            // CSV
            'text/csv',
            // Excel
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ];
        
        const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.csv', '.xlsx'];
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        
        return validTypes.includes(file.type) || validExtensions.includes(fileExtension);
    };

    const handleFileSelect = async (file: File) => {
        if (file && isValidFileType(file)) {
            const detectedFileType = getFileType(file);
            setSelectedFile(file);
            setFileType(detectedFileType);
            setIsUploading(true);
            
            try {
                const formData = new FormData();
                formData.append("file", file);
                
                const response = await fetch(apis.UPLOAD_IMAGE, {
                    method: 'POST',
                    body: formData,
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const result = await response.json();
                setFileUrl(result.data.s3ImageUrl);
                toast.success(`${detectedFileType.toUpperCase()} file uploaded successfully!`);
                console.log('Upload successful:', result);
            } catch (err) {
                console.error('File upload failed:', err);
                toast.error('Upload failed. Please try again.');
            } finally {
                setIsUploading(false);
            }
        } else {
            toast.error('Please select a valid file (Image, CSV, or XLSX)');
        }
    };

    const handleSetImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };
    
    const copyURL = async () => {
        try {
            await navigator.clipboard.writeText(fileUrl);
            toast.success('URL copied to clipboard!');
        } catch (err) {
            if (urlInputRef.current) {
                urlInputRef.current.select();
                urlInputRef.current.setSelectionRange(0, 99999);
                document.execCommand("copy");
                toast.success('URL copied to clipboard!');
            } else {
                toast.error("Unable to copy URL. Please copy it manually.");
            }
        }
    };

    const clearUpload = () => {
        setSelectedFile(null);
        setFileUrl('');
        setFileType('other');
    };

    const getFileIcon = (type: 'image' | 'csv' | 'xlsx' | 'other') => {
        switch (type) {
            case 'image':
                return (
                    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                );
            case 'csv':
                return (
                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                );
            case 'xlsx':
                return (
                    <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                    </svg>
                );
            default:
                return (
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                );
        }
    };


    return (
        <div className="flex mainWrapper w-full">
            <div className="sidenav-wrapper">
                <SideNavbar />
            </div>
            <div className="right-container">
                <div className="header-container">
                    <Header />
                </div>
                <div className="body-container">
                    <div className="Subheader bg-white p-[15px] shadow-[0_2px_6px_rgba(0,0,0,.1)]">
                        <p className="text-xl font-bold text-[#43425D] flex items-center">
                            <h1>File Uploader</h1>
                        </p>
                    </div>

                    {/* Main Upload Container */}
                    <div className="max-w-4xl mx-auto p-6">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                            {/* Header */}
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-xl font-semibold text-gray-800">File Uploader</h2>
                                <p className="text-sm text-gray-600 mt-1">Upload images, CSV, or Excel files</p>
                            </div>

                            {/* Upload Area */}
                            <div className="p-6">
                                {!fileUrl ? (
                                    <div
                                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
                                            dragActive 
                                                ? 'border-blue-400 bg-blue-50' 
                                                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                                        }`}
                                        onDragEnter={handleDrag}
                                        onDragLeave={handleDrag}
                                        onDragOver={handleDrag}
                                        onDrop={handleDrop}
                                    >
                                        {isUploading ? (
                                            <div className="flex flex-col items-center">
                                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                                                <p className="text-gray-600 font-medium">Uploading your file...</p>
                                                <p className="text-sm text-gray-500 mt-1">Please wait</p>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                                                    {selectedFile ? getFileIcon(getFileType(selectedFile)) : (
                                                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                                                        </svg>
                                                    )}
                                                </div>
                                                <h3 className="text-lg font-medium text-gray-700 mb-2">
                                                    {selectedFile ? selectedFile.name : 'Drop your files here'}
                                                </h3>
                                                <p className="text-gray-500 mb-4">
                                                    or click to browse from your computer
                                                </p>
                                                
                                                {/* File Type Indicators */}
                                                <div className="flex justify-center space-x-4 mb-4">
                                                    <div className="flex items-center text-xs text-gray-500">
                                                        <svg className="w-4 h-4 mr-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                                        </svg>
                                                        Images
                                                    </div>
                                                    <div className="flex items-center text-xs text-gray-500">
                                                        <svg className="w-4 h-4 mr-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                                        </svg>
                                                        CSV
                                                    </div>
                                                    <div className="flex items-center text-xs text-gray-500">
                                                        <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                                                        </svg>
                                                        Excel
                                                    </div>
                                                </div>

                                                <label className="inline-flex items-center px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 cursor-pointer transition-colors shadow-sm">
                                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                                                    </svg>
                                                    Choose File
                                                    <input
                                                        type="file"
                                                        className="hidden"
                                                        accept="image/*,.csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                                        onChange={handleSetImage}
                                                    />
                                                </label>
                                                <p className="text-xs text-gray-400 mt-3">
                                                    Supports: JPG, PNG, GIF, WebP, CSV, XLSX (Max 10MB)
                                                </p>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    /* Success State */
                                    <div className="text-center">
                                        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                                            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                            </svg>
                                        </div>
                                        <h3 className="text-lg font-medium text-gray-700 mb-2">Upload Successful!</h3>
                                        <p className="text-gray-500 mb-6">Your {fileType.toUpperCase()} file has been uploaded and is ready to use.</p>
                                        
                                        {/* File Info */}
                                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                            <div className="flex items-center justify-center mb-3">
                                                {getFileIcon(fileType)}
                                                <span className="ml-2 text-sm font-medium text-gray-700">
                                                    {selectedFile?.name} ({fileType.toUpperCase()})
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-500 text-center">
                                                Size: {selectedFile ? (selectedFile.size / 1024).toFixed(1) : '0'} KB
                                            </div>
                                        </div>

                                        {/* URL Display */}
                                        <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">File URL:</label>
                                            <div className="flex items-center space-x-3">
                                                <input
                                                    type="text"
                                                    value={fileUrl}
                                                    ref={urlInputRef}
                                                    readOnly
                                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                                <button
                                                    onClick={copyURL}
                                                    className="inline-flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-sm"
                                                >
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                    </svg>
                                                    Copy URL
                                                </button>
                                            </div>
                                        </div>

                                        {/* File Preview */}
                                        {fileType === 'image' && (
                                            <div className="mb-6">
                                                <img 
                                                    className="max-w-full max-h-96 mx-auto rounded-lg shadow-md border border-gray-200" 
                                                    src={fileUrl} 
                                                    alt="Uploaded preview" 
                                                />
                                            </div>
                                        )}

                                        {/* Download Link for non-image files */}
                                        {(fileType === 'csv' || fileType === 'xlsx') && (
                                            <div className="mb-6">
                                                <a
                                                    href={fileUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                                >
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                                    </svg>
                                                    Download File
                                                </a>
                                            </div>
                                        )}

                                        {/* Actions */}
                                        <div className="flex justify-center space-x-3">
                                            <button
                                                onClick={clearUpload}
                                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                                            >
                                                Upload Another
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FileUploaderComponent;